import React from "react";
import { IAgent, IOffice } from "../../lib/Contact";
import { IListingSummary } from "../../lib/Listing";
import { WsrvImage } from "../Media";
import { EWsrvTransform } from "../../lib";
import {
  ContactCard,
  EContactCardLayout,
  IContactCardProps,
} from "../Contact/ContactCard";
import { IContactFormProps } from "../Forms";
import { ILinkFunc } from "../../lib/util";
import { FiPhone } from "react-icons/fi";
import { RiMailSendLine } from "react-icons/ri";

export enum EListingContactContent {
  agents = "agents",
  office = "office",
}

export interface IListingContactProps {
  listing?: IListingSummary;
  /**
   * Agents to display.
   */
  agents?: IAgent[];
  /**
   * Offices to list.
   */
  offices?: IOffice[];
  /**
   * Contact card extended properties.
   */
  contactCardProps?: Partial<IContactCardProps>;
  /**
   * Contact form extended properties.
   */
  contactFormProps?: Partial<IContactFormProps>;
  /**
   * Linking function component for agents or offices.
   */
  linkFunc?: ILinkFunc;

  contentVariant?: EListingContactContent;

  className?: string;

  showOfficePhone?: boolean;
  showOfficeEmail?: boolean;
}

const OfficeAddress: React.FC<{ office: IOffice; showOfficePhone?: boolean; showOfficeEmail?: boolean; }> = ({
  office, showOfficePhone, showOfficeEmail
}) => (
  <address className="text-center not-italic w-full mt-7 flex flex-col">
    <span className="font-bold">{office.fullName}</span>
    {office.address && (
      <>
        <span>{office.address.street1}</span>
        <span>{`${office.address.city ?? ""} ${office.address.state ?? ""} ${office.address.postal ?? ""}`}</span>
        {showOfficePhone && <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full flex items-center text-xs gap-2">
          <a href={`tel: ${office.phone}`}>
            <FiPhone />
          </a>
          {office.phone}
        </span>}
        {showOfficeEmail && <div className="whitespace-nowrap overflow-hidden text-ellipsis max-w-full flex items-center text-xs gap-2">
          <a href={`mailto: ${office.email}`}>
            <RiMailSendLine />
          </a>
          {office.email}
        </div>}
      </>
    )}
  </address>
);

const OfficeCard: React.FC<{ office: IOffice; showOfficePhone?: boolean; showOfficeEmail?: boolean; linkFunc?: ILinkFunc }> = ({
  office, showOfficePhone = false, showOfficeEmail = false, linkFunc
}) => {
  const Linkf = linkFunc || ((props: any) => <a {...props} />);
  return (
    <div className="relative">
      {office.photos?.length && (
        <WsrvImage
          urlParams={{
            originURL: office.photos[0].url,
            preset: EWsrvTransform.limit,
          }}
          imgProps={{
            className: "max-w-full",
            title: office.fullName,
          }}
          className="max-w-[400px]"
        />
      )}
      <OfficeAddress office={office} showOfficePhone={showOfficePhone} showOfficeEmail={showOfficeEmail} />
      {office.path && (
        <Linkf
          href={office.path}
          className="absolute inset-0"
        />
      )}
    </div>
  )
};

export const ListingContact: React.FC<IListingContactProps> = ({
  listing,
  agents,
  offices,
  className,
  linkFunc,
  contactCardProps,
  contactFormProps,
  contentVariant = EListingContactContent.agents,
  showOfficePhone,
  showOfficeEmail,
}) => {
  return (
    <div className={className}>
      {contentVariant == EListingContactContent.agents ? (
        <div className="flex flex-wrap justify-between mt-3 ">
          {agents?.map((contact, index) => (
            <ContactCard
              className={`w-full sm:w-1/2 mx-auto bg-transparent`}
              contact={contact}
              key={index}
              maxWidthImage={115}
              imageClasses="max-w-[115px] mx-auto"
              layout={EContactCardLayout.vertical}
              linkFunc={linkFunc}
              showVCard={false}
              formProps={contactFormProps}
              {...contactCardProps}
            />
          ))}
          {offices && offices[0] && <OfficeAddress office={offices[0]} showOfficePhone={showOfficePhone} showOfficeEmail={showOfficeEmail} />}
        </div>
      ) : (
        offices && offices[0] && <OfficeCard office={offices[0]} showOfficePhone={showOfficePhone} showOfficeEmail={showOfficeEmail} linkFunc={linkFunc} />
      )}
    </div>
  );
};
